import React, { useEffect, useState } from "react";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button } from "@heroui/react";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const statusColors: Record<string, string> = {
  approved: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  verified: "bg-blue-100 text-blue-800",
  disputed: "bg-orange-100 text-orange-800",
  paid: "bg-emerald-100 text-emerald-800",
};

const VendorInvoiceList = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [vendorId, setVendorId] = useState<any>(null);
  const [dateRange, setDateRange] = useState<any>(null);
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);

  // Fetch vendors for filter dropdown
  const { data: vendorData } = useQuery(
    ["vendorListForFilter"],
    () => ListCommanWay("/admin/vendor/list", {}, { page: 1, paginate: 1000 }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const opts = (res?.data?.data || []).map((v: any) => ({
            value: v.id,
            label: v.vendor_name || v.companyName || `Vendor #${v.id}`,
          }));
          setVendorOptions(opts);
        }
      },
    },
  );

  // Build query
  const buildQuery = () => {
    const q: any = {};
    if (vendorId) q.vendorId = vendorId.value;
    if (dateRange?.start && dateRange?.end) {
      q.invoiceDateFrom = dateRange.start;
      q.invoiceDateTo = dateRange.end;
    }
    return q;
  };

  // Fetch invoices
  const {
    data: invoiceData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    ["vendorInvoiceList", currentPage, vendorId, dateRange],
    () =>
      ListCommanWay(
        "/admin/vendor-invoice/list",
        { query: buildQuery() },
        { page: currentPage, paginate: perPage, sort: { createdAt: -1 }, includes: ["_vendor"] },
      ),
    {
      keepPreviousData: true,
      enabled: false,
    },
  );

  const invoices =
    invoiceData?.status === "SUCCESS" ? invoiceData?.data?.docs || invoiceData?.data?.data || invoiceData?.data?.rows || [] : [];
  const totalPages = invoiceData?.data?.pages || invoiceData?.pagination?.last_page || 1;
  const totalCount = invoiceData?.data?.total || invoiceData?.pagination?.total || 0;

  // Soft delete mutation
  const { mutate: softDelete, isLoading: deleting } = useMutation(
    (id: number) => UpdateCommanWay({ url: `/admin/vendor-invoice/softDelete/${id}`, data: {} }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("Invoice deleted successfully", "success");
          refetch();
        } else {
          UseToast(res?.message || "Failed to delete", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err?.message || "Error deleting invoice", "error");
      },
    },
  );

  const handleDelete = (id: number) => {
    Swal.fire({
      title: "Delete Invoice?",
      text: "This will soft-delete the invoice. Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        softDelete(id);
      }
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [vendorId, dateRange]);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">Vendor Invoices</h1>
        <Button color="primary" onPress={() => navigate("/billing/vendor-invoice/new")} startContent={<PlusIcon className="w-4 h-4" />}>
          New Invoice
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="w-64">
          <label className="text-sm font-medium mb-1 block">Vendor</label>
          <Select
            options={vendorOptions}
            value={vendorId}
            onChange={(val: any) => setVendorId(val)}
            isClearable
            placeholder="All Vendors"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Invoice Date Range</label>
          <AfDateRangePicker
            setStartDate={(d: any) => setDateRange((prev: any) => ({ ...prev, start: d }))}
            setEndDate={(d: any) => setDateRange((prev: any) => ({ ...prev, end: d }))}
          />
        </div>
        <div className="flex items-end">
          <Button
            color="primary"
            onPress={() => refetch()}
            isLoading={isFetching}
            startContent={<MagnifyingGlassIcon className="w-4 h-4" />}
          >
            Fetch
          </Button>
        </div>
      </div>

      {/* Table */}
      {false && isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {["#", "Invoice No", "Vendor", "Invoice Date", "Period", "Total Amt", "Verified Amt", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-gray-400">
                    No invoices found
                  </td>
                </tr>
              ) : (
                invoices.map((inv: any, idx: number) => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm">{(currentPage - 1) * perPage + idx + 1}</td>
                    <td className="px-3 py-2 text-sm font-medium">
                      <Link to={`/billing/vendor-invoice/${inv.id}`} className="text-blue-600 hover:underline">
                        {inv.invoiceNo || "-"}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-sm">{inv.vendor?.vendor_name || inv.vendorId || "-"}</td>
                    <td className="px-3 py-2 text-sm">{inv.invoiceDate ? moment(inv.invoiceDate).format("DD MMM YYYY") : "-"}</td>
                    <td className="px-3 py-2 text-sm">
                      {inv.periodFrom && inv.periodTo
                        ? `${moment(inv.periodFrom).format("DD MMM")} - ${moment(inv.periodTo).format("DD MMM YYYY")}`
                        : "-"}
                    </td>
                    <td className="px-3 py-2 text-sm font-medium">₹{inv.totalAmount?.toLocaleString("en-IN") || "0"}</td>
                    <td className="px-3 py-2 text-sm font-medium">₹{inv.verifiedAmount?.toLocaleString("en-IN") || "0"}</td>
                    <td className="px-3 py-2 text-sm">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status] || "bg-gray-100 text-gray-800"}`}
                      >
                        {inv.status || "N/A"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/billing/vendor-invoice/${inv.id}`)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(inv.id)} className="text-red-500 hover:text-red-700" title="Delete">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-600">
            Page {currentPage} of {totalPages} ({totalCount} total)
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="bordered" isDisabled={currentPage <= 1} onPress={() => setCurrentPage((p) => p - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="bordered" isDisabled={currentPage >= totalPages} onPress={() => setCurrentPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {isFetching && !isLoading && (
        <div className="fixed top-4 right-4 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs">Refreshing...</div>
      )}
    </div>
  );
};

export default VendorInvoiceList;
